import { WALKIE_SEGMENT_MS } from '@itsappy/shared';

/**
 * The walkie talkie's audio, on both sides of the button.
 *
 * Live speech travels as a run of short, self-contained clips rather than a
 * peer connection. That is a deliberate trade: a radio is heard by everyone in
 * the channel at once, including the people who never opened it, and standing
 * up a mesh or an SFU for a two-second "are you there?" costs more than the
 * second of latency the clips add.
 */

/**
 * Same codec order as a voice note: iOS cannot decode WebM/Opus, so prefer an
 * MP4/AAC container wherever the browser can produce one. A Chromium old
 * enough to lack MP4 recording still falls back to WebM — which the phones on
 * the other end will not play, and which is why this is a preference list
 * rather than a hard requirement.
 */
function preferredType(): MediaRecorderOptions | undefined {
  const candidates = ['audio/mp4;codecs=mp4a.40.2', 'audio/mp4', 'audio/aac'];
  for (const mimeType of candidates) {
    if (typeof MediaRecorder !== 'undefined' && MediaRecorder.isTypeSupported(mimeType)) {
      return { mimeType };
    }
  }
  return undefined;
}

function toBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(reader.error ?? new Error('unreadable segment'));
    reader.onload = () => {
      const result = String(reader.result);
      resolve(result.slice(result.indexOf(',') + 1));
    };
    reader.readAsDataURL(blob);
  });
}

export interface WalkieTransmission {
  /** Lets go of the button: the segment in progress is still sent. */
  stop: () => void;
}

/**
 * Holds the microphone open and hands back one finished clip at a time.
 *
 * Each segment is its own `MediaRecorder`, restarted the moment the last one
 * stops, because only the first blob of a timesliced recording carries the
 * container header — every later slice would be undecodable on its own, which
 * is precisely what the far end has to do with it.
 */
export async function startTransmitting(
  onSegment: (segment: { seq: number; mimeType: string; data: string }) => void,
): Promise<WalkieTransmission> {
  const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
  let held = true;
  let seq = 0;
  let timer: number | null = null;
  let recorder: MediaRecorder | null = null;

  const release = () => {
    for (const track of stream.getTracks()) track.stop();
  };

  const runSegment = () => {
    const active = new MediaRecorder(stream, preferredType());
    recorder = active;
    const parts: Blob[] = [];
    active.ondataavailable = (event) => {
      if (event.data.size) parts.push(event.data);
    };
    active.onstop = () => {
      const type = active.mimeType || 'audio/mp4';
      const blob = new Blob(parts, { type });
      if (blob.size) {
        seq += 1;
        const at = seq;
        void toBase64(blob)
          .then((data) => onSegment({ seq: at, mimeType: type, data }))
          .catch(() => {
            /* One unreadable segment is a gap in the audio, not a reason to
               drop the rest of what someone is saying. */
          });
      }
      if (held) runSegment();
      else release();
    };
    active.start();
    timer = window.setTimeout(() => active.stop(), WALKIE_SEGMENT_MS);
  };

  runSegment();

  return {
    stop: () => {
      if (!held) return;
      held = false;
      if (timer !== null) window.clearTimeout(timer);
      // The final partial segment is the end of the sentence: stopping the
      // recorder flushes it, and `onstop` releases the microphone after.
      if (recorder && recorder.state !== 'inactive') recorder.stop();
      else release();
    },
  };
}

/* ------------------------------ listening ------------------------------- */

let context: AudioContext | null = null;
/** When each speaker's audio runs out, on the context's own clock. */
const playingUntil = new Map<string, number>();
/** Sources still queued per speaker, so letting go can cut them short. */
const queued = new Map<string, AudioBufferSourceNode[]>();

function ensureContext(): AudioContext {
  context ??= new AudioContext();
  /* A browser that has never been clicked keeps the context suspended. The
     app is behind a login, so this nearly always resolves; when it does not,
     the banner still shows who is talking. */
  if (context.state === 'suspended') void context.resume();
  return context;
}

/**
 * Plays one received segment, after whatever that speaker already has queued.
 *
 * Scheduling per speaker rather than globally is what keeps two people talking
 * over each other sounding like two people talking over each other, instead of
 * one queue playing them in turns.
 */
export async function playWalkieChunk(
  userId: string,
  mimeType: string,
  data: string,
): Promise<void> {
  const bytes = Uint8Array.from(atob(data), (char) => char.charCodeAt(0));
  const ctx = ensureContext();
  let buffer: AudioBuffer;
  try {
    buffer = await ctx.decodeAudioData(bytes.buffer);
  } catch {
    /* A container this browser cannot decode — an old Chromium's WebM landing
       on Safari. Nothing to play, and nothing worth interrupting the listener
       over either. */
    console.warn(`walkie: cannot decode ${mimeType}`);
    return;
  }
  const source = ctx.createBufferSource();
  source.buffer = buffer;
  source.connect(ctx.destination);
  const startAt = Math.max(ctx.currentTime, playingUntil.get(userId) ?? 0);
  source.start(startAt);
  playingUntil.set(userId, startAt + buffer.duration);
  const live = queued.get(userId) ?? [];
  live.push(source);
  queued.set(userId, live);
  source.onended = () => {
    const rest = (queued.get(userId) ?? []).filter((node) => node !== source);
    if (rest.length) queued.set(userId, rest);
    else queued.delete(userId);
  };
}

/**
 * Forgets a speaker's place in the queue once they let go.
 *
 * Audio already scheduled is left to finish — it is the tail of what they
 * said. Only the clock is dropped, so their next press starts immediately
 * rather than after a gap the size of their last transmission.
 */
export function endWalkiePlayback(userId: string): void {
  playingUntil.delete(userId);
}
